<script setup lang="ts">
import { computed, shallowRef, onBeforeMount } from 'vue';
import { storeToRefs } from 'pinia';
import capitalize from 'lodash/capitalize';
import { useHeadSafe } from '@unhead/vue';
import { useUserContext, useLimitedSessionDuration, ImpersonateBar } from '@ilteducation/ilt-auth';
import config from '@/config/ConfigProvider';
import TopBar from '@/components/base/TopBar.vue';
import BurgerMenu from '@/components/base/BurgerMenu.vue';
import BottomNavigation from '@/components/base/BottomNavigation.vue';
import useSnackbar from '@/composables/use-snackbar';
import useSubscriptions from './foundations/helpers/use-subscriptions';
import { useElementSize } from '@vueuse/core';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import { useClientAnalytics } from '@ilteducation/client-analytics';
import { useLocale } from '@/plugins/translation';

const { t, l } = useLocale();

const basePath = config.value('basePath');
const serviceName = config.value('serviceName');
const countryCode = config.value('countryCode');

const navigationBarRef = shallowRef<HTMLElement | null>(null);
const { height: navigationBarHeight } = useElementSize(navigationBarRef);
const navbarHeight = computed(() => `${navigationBarHeight.value}px`);

useHeadSafe({
  title: capitalize(serviceName),
  link: [
    { rel: 'icon', href: `${basePath}favicon-${serviceName}.ico` },
    { rel: 'manifest', href: `${basePath}${countryCode}.manifest.json` },
    {
      rel: 'apple-touch-icon',
      href: `${basePath}${countryCode === 'se' ? 'begreppa-logo.png' : 'manabua-logo.png'}`,
    },
  ],
  meta: [
    { name: 'apple-mobile-web-app-title', content: capitalize(serviceName) },
    {
      name: 'msapplication-TileImage',
      content: `${basePath}${countryCode === 'se' ? 'begreppa-logo.png' : 'manabua-logo.png'}`,
    },
  ],
});

const clientAnalytics = useClientAnalytics();
const { initializeAnalytics } = clientAnalytics;

const userContext = useUserContext();
const { isLoggedIn } = storeToRefs(userContext);
const subscriptions = useSubscriptions();

const hasAccess = computed(() => {
  return subscriptions.value.length > 0;
});
const currentSubscriptionName = computed(() => {
  if (!subscriptions.value.length) {
    return null;
  }
  return subscriptions.value[0].type;
});

useLimitedSessionDuration('/login');

const { snack } = useSnackbar();

const route = useRoute();
const isControlPanel = computed(() => route.path === '/control-panel');

const { mdAndUp } = useDisplay();

const hideBarsSetting = computed(() => route.matched.find((r) => r.meta.hideBars)?.meta.hideBars);
const barsHidden = computed(
  () => hideBarsSetting.value === 'always' || (hideBarsSetting.value === 'mobile' && !mdAndUp.value),
);

const canShowBars = computed(() => (isLoggedIn || hasAccess.value) && !barsHidden.value);

const showTopBar = computed(() => canShowBars.value);

const showBottomBar = computed(() => canShowBars.value && !['fi', 'no'].includes(countryCode));

const applyTopPadding = computed(() => !isControlPanel.value && showTopBar.value);

// Bottom padding:
// - On control-panel: only on md and up
// - On all other routes: always
const applyBottomPadding = computed(() => (isControlPanel.value ? mdAndUp.value : showBottomBar.value));

onBeforeMount(async () => {
  await initializeAnalytics({
    getCurrentSubscriptionName: async () => currentSubscriptionName.value || '',
  });
});
</script>

<template>
  <v-app class="manabua">
    <ImpersonateBar />
    <TopBar v-if="showTopBar" white />
    <BurgerMenu v-if="isLoggedIn && hasAccess" />
    <v-main
      tabindex="-1"
      class="bg-surface-container"
      :class="{ 'v-main-top-padding': applyTopPadding, 'v-main-bottom-padding': applyBottomPadding }"
    >
      <router-view />
    </v-main>
    <BottomNavigation v-if="showBottomBar" ref="navigationBarRef" />
    <v-snackbar
      v-model="snack.show"
      :color="snack.color"
      :timeout="snack.color === 'error' ? -1 : 5000"
      close-on-back
      close-on-content-click
      style="z-index: 20000"
    >
      {{ snack.message }}
      <template v-if="snack.color === 'error'" #actions>
        <v-btn variant="text" @click="snack.show = false">{{ t(l.label_close) }}</v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<style lang="scss">
@use '@/foundations/theme/styles' as s;

.v-main {
  --main-top-padding: 0px;
  --main-bottom-padding: 0px;
}

.v-main-bottom-padding {
  --main-bottom-padding: max(v-bind(navbarHeight), #{s.$safe-area-bottom});
  padding-bottom: var(--main-bottom-padding) !important;
}

.v-main-top-padding {
  --main-top-padding: #{s.$top-menu-and-safe-area};
  padding-top: var(--main-top-padding) !important;
}

.v-application {
  font-family: inherit !important;
}

body {
  touch-action: pan-x pan-y;
}

.v-application a {
  color: inherit !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease-in;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
